import { CheckCircle, AlertCircle, Loader2, ArrowLeft, RefreshCw } from 'lucide-react';

export type PaymentResultVariant = 'success' | 'error' | 'processing' | 'finalizing';

interface PaymentResultProps {
  variant: PaymentResultVariant;
  amount?: number;
  message?: string;
  /** Overrides the variant's default headline (e.g. "Card Declined" vs "Payment Failed"). */
  headline?: string;
  /** Primary action button label */
  primaryLabel?: string;
  /** Primary action handler */
  onPrimaryAction?: () => void;
  /** Optional secondary button label */
  secondaryLabel?: string;
  /** Optional secondary action handler */
  onSecondaryAction?: () => void;
}

const variantConfig: Record<PaymentResultVariant, {
  icon: React.ReactNode;
  iconBg: string;
  defaultHeadline: string;
  defaultDescription: string;
  defaultPrimaryLabel: string;
}> = {
  success: {
    icon: <CheckCircle className="h-10 w-10 text-green-600" />,
    iconBg: 'bg-green-100',
    defaultHeadline: 'Payment Confirmed!',
    defaultDescription: 'Your payment has been processed and confirmed successfully.',
    defaultPrimaryLabel: 'Back to Job',
  },
  error: {
    icon: <AlertCircle className="h-10 w-10 text-red-500" />,
    iconBg: 'bg-red-100',
    defaultHeadline: 'Payment Failed',
    defaultDescription: 'Your payment could not be processed. Please try again.',
    defaultPrimaryLabel: 'Try Again',
  },
  processing: {
    icon: <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />,
    iconBg: 'bg-blue-100',
    defaultHeadline: 'Payment Processing…',
    defaultDescription: 'Your payment is being processed. This may take a moment.',
    defaultPrimaryLabel: '',
  },
  finalizing: {
    icon: (
      <div className="relative">
        <CheckCircle className="h-10 w-10 text-green-600" />
        <Loader2 className="absolute -bottom-1 -right-1 h-4 w-4 text-blue-500 animate-spin" />
      </div>
    ),
    iconBg: 'bg-green-100',
    defaultHeadline: 'Payment Accepted!',
    defaultDescription: 'Confirming with your bank…',
    defaultPrimaryLabel: '',
  },
};

/**
 * Reusable payment result screen.
 * Centered layout with icon, headline, description, and action buttons.
 * Responsive: full-width buttons, mobile-safe padding, large touch targets.
 */
export function PaymentResult({
  variant,
  amount,
  message,
  headline: headlineOverride,
  primaryLabel,
  onPrimaryAction,
  secondaryLabel,
  onSecondaryAction,
}: PaymentResultProps) {
  const config = variantConfig[variant];
  const headline = headlineOverride ?? config.defaultHeadline;
  const description = message
    ?? (amount != null
      ? `${config.defaultDescription.replace('Your payment', `Your payment of $${amount.toFixed(2)}`)}`
      : config.defaultDescription);
  const btnLabel = primaryLabel ?? config.defaultPrimaryLabel;

  const isInteractive = variant === 'success' || variant === 'error';

  return (
    <div className="flex flex-col items-center justify-center text-center space-y-5 py-6 px-4 min-h-[240px] animate-in fade-in duration-300">
      {/* Icon */}
      <div className={`h-20 w-20 rounded-full flex items-center justify-center ${config.iconBg}`}>
        {config.icon}
      </div>

      {/* Text */}
      <div className="space-y-2 max-w-sm">
        <h3 className="text-xl font-bold text-gray-900">{headline}</h3>
        <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
      </div>

      {/* Actions */}
      {isInteractive && (
        <div className="w-full max-w-xs space-y-3 pt-2">
          {btnLabel && onPrimaryAction && (
            <button
              type="button"
              onClick={onPrimaryAction}
              className={`w-full flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-lg text-sm font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                variant === 'success'
                  ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                  : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
              }`}
            >
              {variant === 'success' && <ArrowLeft className="h-4 w-4" />}
              {variant === 'error' && <RefreshCw className="h-4 w-4" />}
              {btnLabel}
            </button>
          )}
          {secondaryLabel && onSecondaryAction && (
            <button
              type="button"
              onClick={onSecondaryAction}
              className="w-full flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      )}

      {/* Processing / finalizing spinner note */}
      {!isInteractive && (
        <p className="text-xs text-gray-400 pt-1">
          Please do not close this window.
        </p>
      )}
    </div>
  );
}
